import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IColumnMetaData, IReportMetadata, ISubColumns, ReportDataService } from '../report-data.service';
import { FormattingService } from '../../services/formatting.service';
import { ReportFiltersUtil } from '../report-filters-util.service';
import { IDescribedStringObject, IObject, IValueObject } from '../../../../type_definitions/datatypes';
import * as _ from 'underscore';

import { util } from '../../util';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../formly/ga-formly.service';

export interface IReportExportModel {
  possibleColumns:IColumnMetaData[];
  type:IValueObject;
  excelTemplate: IValueObject;
}

@Component({
  selector: 'report-export',
  template: `
    <div class="message-dialog report-export-dialog">

      <div class="modal-header">
        <h4 class="modal-title">{{title}}</h4>
      </div>

      <div class="modal-body">
        <h2 *ngIf="loading">
          <activity-indicator></activity-indicator>
          <span translate>LOADING</span>
        </h2>

        <div *ngIf="!loading">
          <formly [form]="form" [fields]="fields" [model]="model"></formly>
          <collapsible-pane [collapsed]="false" title="Columns">
            <select-report-columns [columns]="model.possibleColumns" columnKey="formatting.defaultinexport" [checkboxColumns]="4" [isInDialog]="true"></select-report-columns>
          </collapsible-pane>
        </div>
      </div>

      <div class="modal-footer footer-margin">
        <button (click)="export()" class="btn btn-success" [disabled]="loading" translate>GA.BUTTONS.EXPORT</button>
        <button (click)="onCancel.emit()" class="btn btn-danger" translate>GA.BUTTONS.CANCEL</button>
      </div>

    </div>
  `,
  styleUrls: ['./report-export.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportExportComponent implements OnInit, OnChanges {
  @Input() public reportId: number;
  @Input() public title: string;
  @Input() public metadata: IReportMetadata;
  @Input() public queryFilters: IObject;

  @Output() public onExport: EventEmitter<IReportExportModel> = new EventEmitter<IReportExportModel>();
  @Output() public onCancel: EventEmitter<void> = new EventEmitter<void>();

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields: IExtraFieldConfigurationObject[];
  public model: IReportExportModel = <any>{};
  public loading: boolean;

  constructor(
    private formattingService: FormattingService,
    private formlyService: GaFormlyService,
    private reportDataService: ReportDataService,
    private reportFiltersUtil: ReportFiltersUtil
  ) {
    /**/
  }

  public ngOnInit():void {
    this.fields = this.createFields();
    this.setData();
  }

  public ngOnChanges(changesObj: SimpleChanges): void {
    if (changesObj.reportId) {
      this.reportId = changesObj.reportId.currentValue;
    }
    if (changesObj.title) {
      this.title = changesObj.title.currentValue;
    }
    if (changesObj.metadata) {
      this.metadata = _.clone(changesObj.metadata.currentValue);
    }
    if (changesObj.queryFilters) {
      this.queryFilters = changesObj.queryFilters.currentValue;
    }
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    const fields: IExtraFieldConfigurationObject[] = [];
    const labelClass: string = 'col-md-2';
    const valueClass: string = 'col-md-5';

    const exportOptions: IDescribedStringObject[] = [
      {
        description: 'Excel',
        id: 'excel'
      }
    ];

    // set default exportOption
    this.model.type = exportOptions[0];

    // only show the Export format field if there are any templates
    if (this.metadata.templates && this.metadata.templates.length) {
      exportOptions.push({
        description: 'Excel with template',
        id: 'excelwithtemplate'
      });

      fields.push(
        this.formlyService.aRadio('type', 'Format')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withOptions(exportOptions)
          .withFormat(this.formattingService.formatLov)
          .build()
      );

      fields.push(
        this.formlyService.aSelect('excelTemplate', 'Template')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withOptions(this.metadata.templates)
          .withFormat(this.formattingService.formatLov)
          .withHideExpression(() => {
            return this.model.type.id !== 'excelwithtemplate';
          })
          .withRequired(true)
          .build()
      );
      this.model.excelTemplate = this.metadata.templates[0];
    }

    return fields;
  }

  private setData(): void {
    const hasSubColumns:boolean = !!_.findWhere(this.metadata.columns, { hasSubColumns: true });

    this.model.possibleColumns = this.metadata.columns.filter((col:IColumnMetaData) => col.formatting && col.formatting.showinexport && (!hasSubColumns || hasSubColumns && !col.hasSubColumns));

    // load subcolumns
    if (hasSubColumns) {
      this.loading = true;
      this.loadSubColumns(this.reportId.toString(), this.metadata, this.queryFilters)
        .then((subColumns:IColumnMetaData[]) => {
          _.each(subColumns, (subcolumn:IColumnMetaData) => {
            this.model.possibleColumns.push(subcolumn);
          });
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  public export():void {
    this.onExport.emit(this.model);
  }

  private loadSubColumns(reportId: string, metadata: IReportMetadata, queryFilters: IObject): Promise<IColumnMetaData[]> {
    const subcolumns: IColumnMetaData[] = [];
    const filters:any = this.reportFiltersUtil.getQueryFiltersFromModel(queryFilters, metadata, {
      applyDefaultValues: false,
      applyNullValues: true
    });

    return this.reportDataService.loadSubColumns(`${reportId}`, reportId, filters, metadata.columns)
      .then(util.spread((subColumns: ISubColumns, compiledSubColumns: IColumnMetaData[]) => {

        _.forEach(compiledSubColumns, (subColumn: IColumnMetaData) => {
          const isSubColumn:boolean = !_.findWhere(metadata.columns, { id: subColumn.id });
          if (isSubColumn && subColumn.formatting && subColumn.formatting.showinexport) {
            subColumn.formattedLabel = this.formattingService.removeHtmlBreaks(subColumn.label, '\n');
            subcolumns.push(subColumn);
          }
        });

        return subcolumns;
      }));
  }
}